import { AutorenewOutlined, WhatsApp } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { FunctionComponent, useEffect, useState } from 'react'

import { MyDialogTitle } from '../../../../base/components/Dialog/MyDialogTitle.tsx'
import useMobileDialog from '../../../../base/hooks/useMobileDialog.ts'
import { useUtils } from '../../../../base/hooks/useUtils.tsx'
import { useWaapi } from '../hooks/useWaapi.ts'
import { esUrlPublicaWaapi } from '../services/waapiError.ts'
import { WaapiDocumento } from '../services/waapiMensaje.ts'
import {
  formatearTelefonoWaapi,
  parseTelefonoWaapi,
  WAAPI_CODIGO_PAIS_DEFAULT,
  WAAPI_CODIGOS_PAIS,
} from '../services/waapiTelefono.ts'

export interface WaapiEnviarDialogProps {
  open: boolean
  /** `enviado` = true si se envió el mensaje */
  onClose: (enviado?: boolean) => void
  /** Título del diálogo. Por defecto "Enviar por WhatsApp" */
  titulo?: string
  /** URL pública del archivo; si no se conoce aún, `obtenerUrl` */
  url?: string | null
  obtenerUrl?: () => Promise<string | null> | string | null
  documento?: WaapiDocumento
  /** Reemplaza la frase del documento en el mensaje, ej. "el reporte de cierre de la Caja 2" */
  descripcion?: string | null
  numero?: string | number | null
  /** Quien recibe: nombre para el saludo y teléfono inicial */
  destinatario?: { nombre?: string | null; telefono?: string | null; codigoPais?: string | null }
  nombreArchivo?: string | null
}

/**
 * Envía un documento por WhatsApp: número (con código de país), mensaje dinámico editable
 * ("Otra redacción" genera uno distinto) y envío con la licencia `lw` de la sesión.
 *
 * Cada vez que se abre, el contenido se vuelve a montar (`key`): el teléfono, el mensaje y la URL se
 * toman en el estado inicial, sin `setState` dentro de un efecto. El contenido sigue montado mientras
 * el diálogo se cierra, así la animación de salida no muestra un diálogo vacío.
 */
const WaapiEnviarDialog: FunctionComponent<WaapiEnviarDialogProps> = (props) => {
  const { dialogProps } = useMobileDialog()

  // Contador de aperturas, ajustado durante el render (patrón de React para derivar de una prop)
  const [abiertoAntes, setAbiertoAntes] = useState(props.open)
  const [apertura, setApertura] = useState(0)
  if (props.open !== abiertoAntes) {
    setAbiertoAntes(props.open)
    if (props.open) setApertura((n) => n + 1)
  }

  return (
    <Dialog open={props.open} fullWidth maxWidth="sm" {...dialogProps}>
      <WaapiEnviarContenido key={apertura} {...props} />
    </Dialog>
  )
}

/** Valores iniciales al abrir: teléfono del destinatario y URL si ya es pública */
const telefonoInicial = (destinatario: WaapiEnviarDialogProps['destinatario']) => {
  const t = parseTelefonoWaapi(destinatario?.telefono, destinatario?.codigoPais)
  return {
    codigoPais: t?.codigoArea ?? (destinatario?.codigoPais || WAAPI_CODIGO_PAIS_DEFAULT),
    telefono: t?.telefono ?? (destinatario?.telefono ?? '').replace(/\D/g, ''),
  }
}

const WaapiEnviarContenido: FunctionComponent<WaapiEnviarDialogProps> = ({
  onClose,
  titulo = 'Enviar por WhatsApp',
  url: urlInicial,
  obtenerUrl,
  documento,
  descripcion,
  numero,
  destinatario,
  nombreArchivo,
}) => {
  const waapi = useWaapi()
  const { toast, showError } = useUtils()

  const nuevoMensaje = () =>
    waapi.generarMensaje({ documento, descripcion, numero, destinatario: destinatario?.nombre })

  const [codigoPais, setCodigoPais] = useState(() => telefonoInicial(destinatario).codigoPais)
  const [telefono, setTelefono] = useState(() => telefonoInicial(destinatario).telefono)
  const [mensaje, setMensaje] = useState(nuevoMensaje)
  const urlPublica = esUrlPublicaWaapi(urlInicial) ? urlInicial : null
  const [url, setUrl] = useState<string | null>(urlPublica)
  // Solo se busca la URL si no llegó una pública y hay cómo obtenerla
  const [buscandoUrl, setBuscandoUrl] = useState(() => !urlPublica && Boolean(obtenerUrl))

  const redactar = () => setMensaje(nuevoMensaje())

  // URL del archivo (ej. el PDF carta del backend): el estado solo cambia cuando llega la respuesta
  useEffect(() => {
    if (urlPublica || !obtenerUrl) return
    let vigente = true
    Promise.resolve()
      .then(() => obtenerUrl())
      .then((u) => {
        if (!vigente) return
        setUrl(esUrlPublicaWaapi(u) ? u : null)
        setBuscandoUrl(false)
      })
      .catch(() => {
        if (!vigente) return
        setUrl(null)
        setBuscandoUrl(false)
      })
    return () => {
      vigente = false
    }
    // Se monta en cada apertura (key): basta con buscarla una vez
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const telefonoValido = parseTelefonoWaapi(telefono, codigoPais)
  const puedeEnviar =
    waapi.licenciaActiva &&
    Boolean(telefonoValido) &&
    Boolean(url) &&
    Boolean(mensaje.trim()) &&
    !waapi.enviando

  const enviar = async () => {
    if (!url || !telefonoValido) return
    try {
      await waapi.enviar({
        telefono,
        codigoPais,
        url,
        mensaje,
        documento,
        numero,
        destinatario: destinatario?.nombre,
        nombreArchivo,
      })
      toast.success(`Enviado por WhatsApp a ${formatearTelefonoWaapi(telefonoValido)}`)
      onClose(true)
    } catch (e) {
      showError(e)
    }
  }

  return (
    <>
      <MyDialogTitle onClose={() => onClose(false)} icon={WhatsApp}>
        {titulo}
      </MyDialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          {!waapi.licenciaActiva && (
            <Grid size={12}>
              <Alert severity="warning">{waapi.licenciaMensaje}</Alert>
            </Grid>
          )}
          {!buscandoUrl && !url && (
            <Grid size={12}>
              <Alert severity="error">
                Este documento solo existe en este navegador y no tiene un enlace público; no se puede enviar
                por WhatsApp.
              </Alert>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              select
              label="País"
              size="small"
              fullWidth
              value={codigoPais}
              disabled={waapi.enviando}
              onChange={(e) => setCodigoPais(e.target.value)}
            >
              {WAAPI_CODIGOS_PAIS.map((c) => (
                <MenuItem key={c.code} value={c.code}>
                  {c.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 8 }}>
            <TextField
              label="Número de WhatsApp"
              placeholder="Ej. 70123456"
              size="small"
              fullWidth
              required
              autoFocus={!telefono}
              autoComplete="off"
              value={telefono}
              disabled={waapi.enviando}
              onChange={(e) => setTelefono(e.target.value.replace(/[^\d+\s-]/g, ''))}
              error={Boolean(telefono) && !telefonoValido}
              helperText={
                telefono && !telefonoValido
                  ? 'Número no válido'
                  : telefonoValido
                    ? `Se enviará a ${formatearTelefonoWaapi(telefonoValido)}`
                    : 'Número sin el código de país'
              }
              slotProps={{
                htmlInput: { inputMode: 'tel' },
                input: { startAdornment: <InputAdornment position="start">+{codigoPais}</InputAdornment> },
              }}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Mensaje"
              size="small"
              fullWidth
              multiline
              minRows={4}
              maxRows={10}
              value={mensaje}
              disabled={waapi.enviando}
              onChange={(e) => setMensaje(e.target.value)}
              helperText="Cada envío lleva una redacción distinta para que WhatsApp no lo considere repetitivo. Puede editarlo."
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                      <Tooltip title="Otra redacción">
                        <span>
                          <IconButton size="small" onClick={redactar} disabled={waapi.enviando}>
                            <AutorenewOutlined fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
          <Grid size={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {buscandoUrl && <CircularProgress size={14} />}
              <Typography variant="caption" color="text.secondary" noWrap>
                {buscandoUrl ? 'Preparando el documento…' : url ? `Archivo: ${nombreArchivo || titulo}` : ''}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center' }}>
        <Button color="error" onClick={() => onClose(false)} disabled={waapi.enviando}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<WhatsApp />}
          loading={waapi.enviando}
          disabled={!puedeEnviar}
          onClick={enviar}
        >
          Enviar
        </Button>
      </DialogActions>
    </>
  )
}

export default WaapiEnviarDialog
